import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

function AnimatedWord({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState<number | "auto">("auto");

  useLayoutEffect(() => {
    if (measureRef.current) {
      setWidth(measureRef.current.getBoundingClientRect().width);
    }
  }, [value]);

  return (
    <motion.span
      animate={{ width }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`relative inline-block overflow-hidden align-bottom ${className ?? ""}`}
    >
      <span
        ref={measureRef}
        aria-hidden
        className="invisible inline-block whitespace-pre"
      >
        {value}
      </span>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={value}
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -8, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="absolute inset-0 inline-block whitespace-pre"
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}

export default function Heading() {
  const { t } = useLingui();

  const phrases = useMemo(
    () => [
      t`ett företag`,
      t`en organisation`,
      t`en produkt`,
      t`en person`,
      t`ett varumärke`,
      t`ett ämne`,
    ],
    [t]
  );

  const reservation = useMemo(
    () => [...phrases].sort((a, b) => a.length - b.length)[2],
    [phrases]
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % phrases.length);
    }, 2200);
    return () => clearInterval(id);
  }, [phrases.length]);

  const [article, ...rest] = phrases[index].split(" ");
  const word = rest.join(" ");

  return (
    <h1 className="text-xl font-semibold">
      <Trans>Bevaka</Trans>{" "}
      <span className="relative inline-block align-bottom">
        <span aria-hidden className="invisible">
          {reservation}
        </span>
        <span className="absolute left-0 top-0 whitespace-nowrap">
          <AnimatedWord value={article} />{" "}
          <AnimatedWord value={word} className="text-muted-foreground" />
        </span>
      </span>
    </h1>
  );
}
